import React, { useState, useCallback } from 'react';
import { Upload, Download, Settings, Zap, FileImage, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { API_CONFIG, getApiUrl, validateFileSize, validateFileType, formatFileSize } from '../config/api';
import FeedbackPrompt from './FeedbackPrompt';
import { useTusUpload, extractTusUploadId } from '../hooks/useTusUpload';

interface CompressionSettings {
  quality: number;
  maxWidth: number | null;
  maxHeight: number | null;
  outputFormat: string;
  progressive: boolean;
  optimize: boolean;
}

interface CompressionPreset {
  name: string;
  description: string;
  quality: number;
  max_width: number | null;
  max_height: number | null;
  output_format: string;
  progressive: boolean;
  optimize: boolean;
}

interface CompressionResult {
  file: File;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  dimensions: string;
  downloadUrl: string;
}

const ImageCompressor: React.FC = () => {
  const [files, setFiles] = useState<File[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<CompressionResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [presets, setPresets] = useState<Record<string, CompressionPreset>>({});
  const [selectedPreset, setSelectedPreset] = useState<string>('web_optimized');
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  const [settings, setSettings] = useState<CompressionSettings>({
    quality: 85,
    maxWidth: 1920,
    maxHeight: 1080,
    outputFormat: 'original',
    progressive: true,
    optimize: true,
  });

  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackType, setFeedbackType] = useState<'success' | 'error'>('success');

  // Resumable TUS upload — used by compressSingleImage
  const { upload: tusUpload, progress: uploadProgress, uploading: isUploading } = useTusUpload();

  // Load presets on component mount
  React.useEffect(() => {
    loadPresets();
  }, []);

  const loadPresets = async () => {
    try {
      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.COMPRESSION_PRESETS));
      if (response.ok) {
        const data = await response.json();
        setPresets(data.presets);
      }
    } catch (error) {
      console.error('Failed to load presets:', error);
    }
  };

  const applyPreset = (presetKey: string) => {
    const preset = presets[presetKey];
    if (preset) {
      setSettings({
        quality: preset.quality,
        maxWidth: preset.max_width,
        maxHeight: preset.max_height,
        outputFormat: preset.output_format,
        progressive: preset.progressive,
        optimize: preset.optimize,
      });
      setSelectedPreset(presetKey);
    }
  };

  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles) return;

    const validFiles: File[] = [];
    const errors: string[] = [];

    Array.from(selectedFiles).forEach((file) => {
      if (!validateFileType(file)) {
        errors.push(`${file.name}: Unsupported file type`);
        return;
      }
      if (!validateFileSize(file)) {
        errors.push(`${file.name}: File too large (max ${formatFileSize(API_CONFIG.MAX_FILE_SIZE)})`);
        return;
      }
      validFiles.push(file);
    });

    if (errors.length > 0) {
      setError(errors.join(', '));
    } else {
      setError(null);
    }

    setFiles(validFiles);
    setResults([]);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const compressImages = async () => {
    if (files.length === 0) return;

    setIsProcessing(true);
    setError(null);
    setResults([]);

    try {
      if (files.length === 1) {
        // Single file compression
        const result = await compressSingleImage(files[0]);
        setResults([result]);
      } else {
        // Batch compression
        await compressBatchImages();
      }
      // Show success feedback prompt
      setFeedbackType('success');
      setShowFeedback(true);
    } catch (error) {
      console.error('Compression error:', error);
      setError(error instanceof Error ? error.message : 'Compression failed');
      // Show error feedback prompt
      setFeedbackType('error');
      setShowFeedback(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const compressSingleImage = async (file: File): Promise<CompressionResult> => {
    // 1. Resumable upload via TUS — survives network drops + refresh
    const tusUrl = await tusUpload(file);
    const uploadId = extractTusUploadId(tusUrl);

    // 2. Trigger server-side compression on the uploaded bytes
    const formData = new FormData();
    formData.append('operation', 'compress_image');
    formData.append('filename', file.name.replace(/\.[^.]+$/, ''));
    formData.append('quality', settings.quality.toString());
    if (settings.maxWidth) formData.append('max_width', settings.maxWidth.toString());
    if (settings.maxHeight) formData.append('max_height', settings.maxHeight.toString());
    formData.append('output_format', settings.outputFormat);

    const response = await fetch(`${API_CONFIG.BASE_URL}/tus/process/${uploadId}`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Compression failed');
    }

    // Debug: Log all response headers
    console.log('Response headers:');
    response.headers.forEach((value, key) => {
      console.log(`${key}: ${value}`);
    });

    // Get compression info from headers with better error handling
    const originalSizeHeader = response.headers.get('X-Original-Size') || response.headers.get('x-original-size');
    const compressedSizeHeader = response.headers.get('X-Compressed-Size') || response.headers.get('x-compressed-size');
    const compressionRatioHeader = response.headers.get('X-Compression-Ratio') || response.headers.get('x-compression-ratio');
    const dimensionsHeader = response.headers.get('X-Image-Dimensions') || response.headers.get('x-image-dimensions');

    console.log('Header values:', {
      originalSizeHeader,
      compressedSizeHeader,
      compressionRatioHeader,
      dimensionsHeader
    });

    const originalSize = parseInt(originalSizeHeader || '0');
    const compressedSize = parseInt(compressedSizeHeader || '0');
    const compressionRatio = parseFloat(compressionRatioHeader || '0');
    const dimensions = dimensionsHeader || '';

    console.log('Parsed values:', {
      originalSize,
      compressedSize,
      compressionRatio,
      dimensions
    });

    // Create blob and download URL
    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);

    // If headers failed, try to get file size from blob
    const actualCompressedSize = compressedSize || blob.size;
    const actualOriginalSize = originalSize || file.size;
    const actualCompressionRatio = compressionRatio || ((1 - actualCompressedSize / actualOriginalSize) * 100);

    console.log('Final values:', {
      actualOriginalSize,
      actualCompressedSize,
      actualCompressionRatio
    });

    return {
      file: new File([blob], file.name, { type: blob.type }),
      originalSize: actualOriginalSize,
      compressedSize: actualCompressedSize,
      compressionRatio: actualCompressionRatio,
      dimensions,
      downloadUrl,
    };
  };

  const compressBatchImages = async () => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    formData.append('quality', settings.quality.toString());
    if (settings.maxWidth) formData.append('max_width', settings.maxWidth.toString());
    if (settings.maxHeight) formData.append('max_height', settings.maxHeight.toString());
    formData.append('output_format', settings.outputFormat);
    formData.append('progressive', settings.progressive.toString());
    formData.append('optimize', settings.optimize.toString());

    const response = await fetch(getApiUrl('/imagetool/compress/batch'), {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Batch compression failed');
    }

    // Get compression info from headers with better error handling
    const totalFiles = response.headers.get('X-Total-Files') || response.headers.get('x-total-files') || '0';
    const originalSizeHeader = response.headers.get('X-Original-Size') || response.headers.get('x-original-size');
    const compressedSizeHeader = response.headers.get('X-Compressed-Size') || response.headers.get('x-compressed-size');
    const compressionRatioHeader = response.headers.get('X-Compression-Ratio') || response.headers.get('x-compression-ratio');

    console.log('Batch compression headers:', {
      totalFiles,
      originalSizeHeader,
      compressedSizeHeader,
      compressionRatioHeader
    });

    const originalSize = parseInt(originalSizeHeader || '0');
    const compressedSize = parseInt(compressedSizeHeader || '0');
    const compressionRatio = parseFloat(compressionRatioHeader || '0');

    // Create download for ZIP file
    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);
    
    // If headers failed, use blob size as fallback
    const actualCompressedSize = compressedSize || blob.size;
    const actualOriginalSize = originalSize || files.reduce((sum, file) => sum + file.size, 0);
    const actualCompressionRatio = compressionRatio || ((1 - actualCompressedSize / actualOriginalSize) * 100);
    
    // Create a single result for the ZIP file
    const zipResult: CompressionResult = {
      file: new File([blob], 'compressed_images.zip', { type: 'application/zip' }),
      originalSize: actualOriginalSize,
      compressedSize: actualCompressedSize,
      compressionRatio: actualCompressionRatio,
      dimensions: `${totalFiles} files`,
      downloadUrl,
    };

    setResults([zipResult]);
  };

  const downloadResult = (result: CompressionResult) => {
    const link = document.createElement('a');
    link.href = result.downloadUrl;
    link.download = result.file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const clearFiles = () => {
    setFiles([]);
    setResults([]);
    setError(null);
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-orange-100 rounded-full">
            <Zap className="w-8 h-8 text-orange-600" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Image Compression</h2>
        <p className="text-gray-600">
          Reduce file sizes while maintaining quality. Perfect for web, email, and storage optimization.
        </p>
      </div>

      {/* Preset Selection */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-3">Compression Presets</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(presets).map(([key, preset]) => (
            <button
              key={key}
              onClick={() => applyPreset(key)}
              className={`p-3 rounded-lg border text-left transition-all ${
                selectedPreset === key
                  ? 'border-orange-500 bg-orange-50 text-orange-900'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="font-medium">{preset.name}</div>
              <div className="text-sm text-gray-600 mt-1">{preset.description}</div>
              <div className="text-xs text-gray-500 mt-2">
                Quality: {preset.quality}% | Max: {preset.max_width || 'Original'}×{preset.max_height || 'Original'}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Advanced Settings */}
      <div className="bg-white border rounded-lg">
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50"
        >
          <div className="flex items-center">
            <Settings className="w-5 h-5 text-gray-500 mr-2" />
            <span className="font-medium">Advanced Settings</span>
          </div>
          <span className="text-gray-400">{showAdvanced ? '−' : '+'}</span>
        </button>
        
        {showAdvanced && (
          <div className="border-t p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quality: {settings.quality}%
                </label>
                <input
                  type="range"
                  min="1"
                  max="100"
                  value={settings.quality}
                  onChange={(e) => setSettings(prev => ({ ...prev, quality: parseInt(e.target.value) }))}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Output Format
                </label>
                <select
                  value={settings.outputFormat}
                  onChange={(e) => setSettings(prev => ({ ...prev, outputFormat: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="original">Keep Original</option>
                  <option value="jpeg">JPEG</option>
                  <option value="png">PNG</option>
                  <option value="webp">WebP</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Width (px)
                </label>
                <input
                  type="number"
                  value={settings.maxWidth || ''}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    maxWidth: e.target.value ? parseInt(e.target.value) : null 
                  }))}
                  placeholder="Original"
                  className="w-full p-2 border border-gray-300 rounded-md"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Height (px)
                </label>
                <input
                  type="number"
                  value={settings.maxHeight || ''}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    maxHeight: e.target.value ? parseInt(e.target.value) : null 
                  }))}
                  placeholder="Original"
                  className="w-full p-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>
            
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.progressive}
                  onChange={(e) => setSettings(prev => ({ ...prev, progressive: e.target.checked }))}
                  className="mr-2"
                />
                Progressive JPEG
              </label>
              
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.optimize}
                  onChange={(e) => setSettings(prev => ({ ...prev, optimize: e.target.checked }))}
                  className="mr-2"
                />
                Optimize
              </label>
            </div>
          </div>
        )}
      </div>

      {/* File Upload */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors"
      >
        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Drop images here or click to select
        </h3>
        <p className="text-gray-600 mb-4">
          Supports PNG, JPG, WebP, BMP, TIFF up to {formatFileSize(API_CONFIG.MAX_FILE_SIZE)}
        </p>
        <input
          type="file"
          multiple
          accept={API_CONFIG.SUPPORTED_FORMATS.INPUT.join(',')}
          onChange={(e) => handleFileSelect(e.target.files)}
          className="hidden"
          id="file-upload"
        />
        <label
          htmlFor="file-upload"
          className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 cursor-pointer"
        >
          <FileImage className="w-4 h-4 mr-2" />
          Select Images
        </label>
      </div>

      {/* Selected Files */}
      {files.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-medium">Selected Files ({files.length})</h3>
            <button
              onClick={clearFiles}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear All
            </button>
          </div>
          <div className="space-y-2">
            {files.map((file, index) => (
              <div key={index} className="flex items-center justify-between p-2 bg-white rounded border">
                <div className="flex items-center">
                  <FileImage className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-sm">{file.name}</span>
                </div>
                <span className="text-xs text-gray-500">{formatFileSize(file.size)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
          <div className="text-red-700">{error}</div>
        </div>
      )}

      {/* Compress Button */}
      {files.length > 0 && (
        <button
          onClick={compressImages}
          disabled={isProcessing}
          className="w-full bg-orange-600 text-white py-3 px-4 rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center justify-center"
        >
          {isProcessing ? (
            <>
              <span className="flex items-center">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {isUploading ? `Uploading ${uploadProgress.toFixed(0)}%` : 'Compressing...'}
              </span>
              {isUploading && (
                <div className="w-full mt-2 bg-white/30 rounded-full h-1 overflow-hidden">
                  <div className="bg-white h-1 transition-all duration-150" style={{ width: `${uploadProgress}%` }} />
                </div>
              )}
            </>
          ) : (
            <span className="flex items-center">
              <Zap className="w-4 h-4 mr-2" />
              Compress {files.length} Image{files.length > 1 ? 's' : ''}
            </span>
          )}
        </button>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center mb-3">
            <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
            <h3 className="font-medium text-green-900">Compression Complete!</h3>
          </div>
          
          {results.map((result, index) => (
            <div key={index} className="bg-white rounded-lg p-4 mb-3 last:mb-0">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">{result.file.name}</span>
                <button
                  onClick={() => downloadResult(result)}
                  className="flex items-center px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Original:</span>
                  <div className="font-medium">{formatFileSize(result.originalSize)}</div>
                </div>
                <div>
                  <span className="text-gray-600">Compressed:</span>
                  <div className="font-medium">{formatFileSize(result.compressedSize)}</div>
                </div>
                <div>
                  <span className="text-gray-600">Saved:</span>
                  <div className="font-medium text-green-600">{result.compressionRatio.toFixed(1)}%</div>
                </div>
                <div>
                  <span className="text-gray-600">Dimensions:</span>
                  <div className="font-medium">{result.dimensions}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Feedback Prompt */}
      <FeedbackPrompt
        isVisible={showFeedback}
        onClose={() => setShowFeedback(false)}
        operationType={feedbackType}
        toolName="Image Compressor"
      />
    </div>
  );
};

export default ImageCompressor;